// src/components/GameScreen.js
import React, { useState, useEffect, useCallback } from 'react';

// Impor semua komponen anak yang dibutuhkan
import PlayerUI from './PlayerUI';
import WinnerModal from './WinnerModal';
import PanjatPinangVisual from './PanjatPinangVisual';
import BalapKarungVisual from './BalapKarungVisual';
import TugOfWarVisual from './TugOfWarVisual';

// Impor logika game dari file terpisah
import { generateQuestion, calculateAnswer } from '../gameLogics/questionGenerator';

// --- Konfigurasi Statis ---
const GAME_CONFIG = {
    'panjat-pinang': { winningScore: 7 },
    'balap-karung': { winningScore: 10 },
    'tarik-tambang': { pullStrength: 10, winThreshold: 50 },
};
// PERBAIKAN: Konstanta untuk durasi animasi, disinkronkan dengan App.css
const ANIMATION_DELAY = 500;

/**
 * Komponen GameScreen adalah inti dari permainan, mengelola state, logika,
 * dan merender semua elemen yang terlihat selama gameplay.
 */
function GameScreen({ gameType, mathMode, level, onPlayAgain }) {
    const [leftPlayer, setLeftPlayer] = useState({ question: '', answer: '', score: 0, status: '' });
    const [rightPlayer, setRightPlayer] = useState({ question: '', answer: '', score: 0, status: '' });
    const [ropePosition, setRopePosition] = useState(0);
    const [winner, setWinner] = useState(null);

    const resetGame = useCallback(() => {
        setLeftPlayer({ question: generateQuestion(mathMode, level), answer: '', score: 0, status: '' });
        setRightPlayer({ question: generateQuestion(mathMode, level), answer: '', score: 0, status: '' });
        setRopePosition(0);
        setWinner(null);
    }, [mathMode, level]);

    useEffect(() => {
        resetGame();
    }, [resetGame]);

    useEffect(() => {
        if (gameType === 'tarik-tambang' && !winner) {
            const { winThreshold } = GAME_CONFIG[gameType];
            if (ropePosition <= -winThreshold) {
                setWinner({ name: 'Pemain Kiri', message: 'Anda berhasil menarik tali!' });
            } else if (ropePosition >= winThreshold) {
                setWinner({ name: 'Pemain Kanan', message: 'Anda berhasil menarik tali!' });
            }
        }
    }, [ropePosition, gameType, winner]);

    const handleCheckAnswer = (player) => {
        const isLeft = player === 'left';
        const playerState = isLeft ? leftPlayer : rightPlayer;
        const setPlayerState = isLeft ? setLeftPlayer : setRightPlayer;

        // Jangan proses jawaban jika sudah ada pemenang
        if (winner) return;

        const correctAnswer = calculateAnswer(playerState.question);
        const playerAnswer = parseFloat(playerState.answer);

        if (playerAnswer === correctAnswer) {
            setPlayerState(prev => ({ ...prev, status: 'correct' }));

            setTimeout(() => {
                let newScore = playerState.score + 1;

                if (gameType === 'tarik-tambang') {
                    const { pullStrength } = GAME_CONFIG[gameType];
                    setRopePosition(pos => pos + (isLeft ? -pullStrength : pullStrength));
                } else {
                    if (newScore >= GAME_CONFIG[gameType].winningScore) {
                        setWinner({
                            name: `Pemain ${isLeft ? 'Kiri' : 'Kanan'}`,
                            message: `Selamat, Anda mencapai ${gameType === 'panjat-pinang' ? 'puncak' : 'garis finis'}!`,
                        });
                        // Hanya update skor terakhir dan status, jangan buat soal baru
                        setPlayerState(prev => ({ ...prev, score: newScore, status: '' }));
                        return;
                    }
                }

                // PERBAIKAN: Gunakan fungsional setState untuk menghindari stale state
                setPlayerState(prev => ({
                    ...prev,
                    score: gameType === 'tarik-tambang' ? prev.score : newScore,
                    status: '',
                    answer: '',
                    question: generateQuestion(mathMode, level),
                }));
            }, ANIMATION_DELAY);

        } else { // Jika jawaban salah
            setPlayerState(prev => ({ ...prev, status: 'wrong' }));

            setTimeout(() => {
                // PERBAIKAN: Gabungkan semua update state dalam satu panggilan fungsional
                setPlayerState(prev => {
                    let newScore = prev.score;
                    // Terapkan penalti hanya untuk Panjat Pinang
                    if (gameType === 'panjat-pinang' && prev.score > 0) {
                        newScore = prev.score - 1;
                    }

                    return {
                        ...prev,
                        score: newScore,
                        status: '',
                        answer: '',
                        question: generateQuestion(mathMode, level),
                    };
                });
            }, ANIMATION_DELAY);
        }
    };

    const renderGameVisual = () => {
        switch (gameType) {
            case 'tarik-tambang':
                // PERBAIKAN: Gunakan komponen visual yang telah diimpor
                return <TugOfWarVisual ropePosition={ropePosition} />;
            case 'panjat-pinang':
                return <PanjatPinangVisual leftScore={leftPlayer.score} rightScore={rightPlayer.score} />;
            case 'balap-karung':
                return <BalapKarungVisual leftScore={leftPlayer.score} rightScore={rightPlayer.score} leftStatus={leftPlayer.status} rightStatus={rightPlayer.status} />;
            default:
                return <div className="game-field">Memuat Visual Game...</div>;
        }
    };

    const gameContainerClass = `${gameType}-game`;

    return (
        <div id={`gameScreen-${gameType}`} className={`game-container ${gameContainerClass}`} style={{ display: 'flex' }}>
            {winner && (
                <WinnerModal 
                    winner={winner.name} 
                    message={winner.message} 
                    onPlayAgain={onPlayAgain} 
                />
            )}
            
            <PlayerUI 
                player="left"
                gameType={gameType}
                mathMode={mathMode}
                playerState={leftPlayer} 
                setPlayerState={setLeftPlayer}
                onCheckAnswer={() => handleCheckAnswer('left')}
            />
            
            <div className="center-section">
                <h1>{gameType.replace('-', ' ').toUpperCase()}</h1>
                {renderGameVisual()}
                <p className="instruction">Jawab soal untuk menang!</p>
            </div>

            <PlayerUI
                player="right"
                gameType={gameType}
                mathMode={mathMode}
                playerState={rightPlayer}
                setPlayerState={setRightPlayer}
                onCheckAnswer={() => handleCheckAnswer('right')}
            />
        </div>
    );
}

export default GameScreen;